// Shared postMessage protocol between the Livery shell (parent) and the embedded
// host app (iframe). Framework-agnostic: the shell side uses useShellBridge, the
// app side uses connectLeestoApp (both in this package).

import type { DiagnosticsSnapshot } from "./diagnostics";

/** Version du protocole parlée par CE code. À incrémenter quand un message est
 *  retiré, renommé, ou change de sens — jamais pour un simple ajout de champ
 *  optionnel, que l'autre bout ignore sans dommage. */
export const LEESTO_BRIDGE_VERSION = 1;

/** Version minimale qu'un connecteur doit parler pour que le shell le pilote.
 *  Tant qu'elle vaut LEESTO_BRIDGE_VERSION, aucun connecteur n'est rejeté. */
export const MIN_SUPPORTED_BRIDGE_VERSION = 1;

/** Verdict de compatibilité entre le shell et le connecteur embarqué. */
export type BridgeCompatibility =
  | { status: "ok"; version: number }
  /** Le connecteur est trop vieux : les fonctions qui exigent le protocole
   *  récent doivent être désactivées ET expliquées, pas laissées mortes. */
  | { status: "connector-outdated"; version: number; required: number }
  /** Le connecteur devance le shell. Informatif : le shell ignore les champs
   *  qu'il ne connaît pas, on ne casse pas un client en avance de phase. */
  | { status: "shell-outdated"; version: number; supported: number };

/** Compare la version annoncée par le connecteur à celle du shell.
 *
 *  Pourquoi cette fonction existe : le connecteur et le shell partaient
 *  toujours ensemble (le Lab sert le bundle), donc ils ne pouvaient pas
 *  diverger. Dès qu'un client épingle une version npm, son connecteur se fige
 *  pendant que le shell évolue — et un désaccord de protocole échoue en
 *  SILENCE (le message est simplement ignoré). Annoncer sa version au
 *  handshake est ce qui transforme cette panne muette en message actionnable.
 *
 *  Une annonce absente vaut 1 : c'est ce que parlent les builds antérieurs à
 *  la négociation. Une valeur aberrante y retombe aussi, plutôt que de
 *  propager un NaN dans les comparaisons. */
export function bridgeCompatibility(
  reported: number | undefined,
  {
    shell = LEESTO_BRIDGE_VERSION,
    min = MIN_SUPPORTED_BRIDGE_VERSION,
  }: { shell?: number; min?: number } = {},
): BridgeCompatibility {
  const version =
    typeof reported === "number" && Number.isFinite(reported) && reported >= 1
      ? Math.floor(reported)
      : 1;
  if (version < min) return { status: "connector-outdated", version, required: min };
  if (version > shell) return { status: "shell-outdated", version, supported: shell };
  return { status: "ok", version };
}

/** An element the tester pointed at in the host app. */
export type PickedElement = {
  /** Best-effort CSS selector to re-find the element. */
  selector: string;
  /** Short human label (tag + text snippet) for display. */
  label: string;
  /** Viewport-relative bounding box at pick time. */
  rect: { x: number; y: number; width: number; height: number };
};

/** Build descriptor of the embedded host app, declared by connectLeestoApp.
 *  Both fields are optional/best-effort — the shell degrades to URL-only. */
export type AppBuild = {
  version?: string;
  environment?: string;
};

/** Messages the host app sends UP to the shell. */
export type AppToShell =
  // `gpc: true` ⇒ le signal Global Privacy Control est actif côté utilisateur :
  // l'enregistrement de session est légalement bloqué (cf. ECH-290).
  | {
      type: "leesto:ready";
      url: string;
      /** Version du protocole parlée par le connecteur. Optionnelle : absente
       *  des builds antérieurs à la négociation, qui parlent la version 1. */
      bridgeVersion?: number;
      /** Favicon declared by the embedded app, resolved to an absolute URL. */
      favicon?: string;
      build?: AppBuild;
      gpc?: boolean;
      diagnosticRunId?: string;
    }
  | { type: "leesto:location"; url: string }
  | { type: "leesto:pong" }
  | ({ type: "leesto:element-picked" } & PickedElement)
  | { type: "leesto:pick-cancelled" }
  | { type: "leesto:screenshot"; dataUrl: string }
  | { type: "leesto:capture-failed"; reason?: string }
  // Session recording (rrweb) — events streamés par batchs pendant l'enregistrement.
  | { type: "leesto:rec-batch"; events: unknown[] }
  | { type: "leesto:rec-stopped"; durationMs: number }
  | { type: "leesto:rec-error"; reason: string }
  // Enregistrement refusé AVANT tout démarrage (aucune donnée captée). `gpc` =
  // Global Privacy Control actif : conformité, la capture ne doit pas avoir lieu.
  | { type: "leesto:rec-blocked"; reason: "gpc" }
  // Réponse à `leesto:diagnostics-request` : copie des ring buffers console +
  // réseau. Toujours envoyée, même vide (diagnostic non armé) — le shell ne doit
  // pas rester suspendu sur son timeout au moment du submit.
  | { type: "leesto:diagnostics"; snapshot: DiagnosticsSnapshot };

/** Messages the shell sends DOWN to the host app. */
export type ShellToApp =
  // Émis par le shell dès qu'il écoute. Le connecteur répond `leesto:pong` ET
  // rejoue `leesto:ready` : l'iframe SSR peut avoir annoncé son état avant que
  // le shell hydrate, sans ça le handshake initial est perdu.
  | { type: "leesto:ping" }
  | { type: "leesto:highlight"; selector: string | null }
  | { type: "leesto:pick-start" }
  | { type: "leesto:pick-stop" }
  | { type: "leesto:capture-request" }
  // Session recording (rrweb).
  | { type: "leesto:rec-start" }
  | { type: "leesto:rec-stop" }
  // Demande du diagnostic technique au moment du signalement (pull, calqué sur
  // `leesto:capture-request`) : aucun trafic postMessage en régime permanent.
  | { type: "leesto:diagnostics-request" };

export type LeestoMessage = AppToShell | ShellToApp;

/** Cheap structural guard — every message type is namespaced with `leesto:`. */
export function isLeestoMessage(data: unknown): data is LeestoMessage {
  return (
    typeof data === "object" &&
    data !== null &&
    typeof (data as { type?: unknown }).type === "string" &&
    (data as { type: string }).type.startsWith("leesto:")
  );
}
